/**
 * The panel's navigation (firm-model spec §10.2, restyled at A8 per the Figma frames, Dan
 * 2026-10-02). The wordmark goes Home, so the sidebar lists the other pages: Team Members,
 * Brand Configuration, Component Library, Activity Log. Plan & billing and Account live in
 * the account menu. Members see Brand Configuration (read-only) and Component Library; admin
 * pages are hidden for them. A firm that can't have a team yet still sees Team Members, whose
 * empty state is its upgrade path (R17; the sidebar's "Team plan" hint was removed, Dan
 * 2026-10-04). Visibility follows capabilities, never role names (spec §4.2).
 * The names are the panel's own: the add-in keeps "Preferences" (Dan 2026-10-02).
 */
import type { Me } from "@contract/firm/me";

type NavKey = "members" | "standards" | "library" | "activity";

type NavItem = { key: NavKey; label: string; to: string };

/** A firm can have members on Team (seat billing, A2) or on the beta (spec §5.3). */
export function firmCanHaveTeam(me: Me): boolean {
  return me.isBeta || me.seats.perSeat;
}

export function navItems(me: Me): NavItem[] {
  if (me.status !== "active") return [];
  const items: NavItem[] = [];
  if (me.capabilities.includes("manage_members")) {
    // A firm of one still reaches Team Members: its empty state is the upgrade path (R17).
    items.push({ key: "members", label: "Team Members", to: "/members" });
  }
  // Everyone in the firm: admins edit, members read (A3, spec §6.2).
  items.push({ key: "standards", label: "Brand Configuration", to: "/standards" });
  // Everyone: the firm library and your own saved components; admins rename and remove (A5).
  items.push({ key: "library", label: "Component Library", to: "/library" });
  // What members ran and did (A6, spec §9): admins only, a firm of one included (its own).
  if (me.capabilities.includes("view_activity")) {
    items.push({ key: "activity", label: "Activity Log", to: "/activity" });
  }
  return items;
}

type AccountMenuItem = { label: string; to: string };

/** The account menu's pages: Plan & billing for billing admins, then Account. Sign out
 * follows them. A disabled account still reaches Account (to delete itself). */
export function accountMenuItems(me: Me | null): AccountMenuItem[] {
  const items: AccountMenuItem[] = [];
  if (me && me.status === "active" && me.capabilities.includes("manage_billing")) {
    items.push({ label: "Plan & Billing", to: "/billing" });
  }
  items.push({ label: "Account", to: "/account" });
  return items;
}
